import { ChevronRight, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router";
import { NavFlyout } from "./NavFlyout";
import type { UsageDto } from "@/shared/api/types";
import { activeNavItemId, navDisabledReason, type NavItem } from "@/shared/constants/nav";
import { ROUTES, SETTINGS_TABS } from "@/shared/constants/routes";
import { useDevicesQuery } from "@/shared/hooks/useDevicesQuery";
import { useEntitlementsQuery } from "@/shared/hooks/useEntitlements";
import { useWorkspaces } from "@/shared/hooks/useWorkspaces";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/ui/cn";
import { Icon } from "@/shared/ui/icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/tooltip";
import { Brand } from "./Brand";
import { ThemeToggle } from "./ThemeToggle";
import { UserMenu } from "./UserMenu";
import { WorkspaceStrip } from "./WorkspaceStrip";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  /** Drawer mode (under 1024 px): full width, no collapse control. */
  drawer?: boolean;
  onNavigate?: (() => void) | undefined;
  className?: string | undefined;
}

function NavEntry({
  item,
  active,
  collapsed,
  badge,
  drawer,
  onNavigate,
  pathname,
  usage,
}: {
  item: NavItem;
  /**
   * Decided for the whole menu at once by `activeNavItemId`, never by the link itself. `NavLink`'s
   * `isActive` is true for a parent as well as its child, which is the defect this prop replaces; it
   * is also where `aria-current` came from, so that is set here too rather than being left to a
   * component that no longer knows the answer.
   */
  active: boolean;
  collapsed: boolean;
  /*
   * Board `1a` also puts an event count on the Events entry. It is not here, and the reason is worth
   * writing down: the only honest sources are a query on every screen — a number nobody navigates by,
   * bought with a request everywhere — or the Overview's cached rollup, which makes the badge appear
   * or vanish depending on which page you happened to open first. A nav that changes shape with
   * navigation history is the same defect as one that changes shape with row counts.
   *
   * The Devices badge stays because it is different in kind: it comes from the devices query the shell
   * already makes for the connection state, so it is always present and always right, and "a terminal
   * needs you" is worth a glance in a way "126 events" is not.
   */

  /** A count worth seeing without opening the page, and whether it is something to act on. */
  badge?: { value: string | number; tone: "quiet" | "warning" } | undefined;
  /** Which page is being read, so the flyout can mark its own entry. */
  pathname: string;
  /** Under 1024 px these rows are touched, not clicked, so they take the touch height. */
  drawer: boolean;
  onNavigate?: (() => void) | undefined;
  usage: UsageDto | undefined;
}) {
  /*
    Shell refresh (2026-09-22): the row is a 6 px-radius band (`--radius-md`). The current entry takes the accent
    tint with the accent label and icon; the others are full-strength labels over muted icons (a nav
    of muted labels reads as a nav of disabled things) and lift to a sheet of `--surface` on hover —
    two different materials for two different facts, so a hovered row can never be mistaken for the
    current one.
  */
  const base =
    "flex items-center gap-2.5 rounded-md px-2.5 text-cell transition-colors duration-120 no-underline";
  const disabledReason = navDisabledReason(item, usage);
  const content = (
    <>
      <Icon icon={item.icon} size={16} className={active ? "text-accent-fg" : "text-fg-muted"} />
      {!collapsed ? <span className="truncate">{item.label}</span> : null}
      {/*
        One badge, and only where it is both cheap and actionable: terminals needing attention. The
        board also counts events on the Events entry, which would make the sidebar refetch the rollup
        summary on every page — a number nobody navigates by, at the cost of a query everywhere.
      */}
      {!collapsed && item.family ? (
        // Board `1a`: Reports is the one entry that leads somewhere with branches, and the chevron
        // says so before the click. The six questions are on the report's own header flyout.
        <Icon icon={ChevronRight} size={16} className="ml-auto size-3.5 shrink-0 text-fg-faint" />
      ) : null}
      {!collapsed && badge ? (
        // Board `1a`: a count rides on the entry in mono, and only turns into a warning when it is
        // something to act on. A bare number teaches the reader nothing about whether to care.
        <span
          // Decorative: it must not join the link's accessible name, or the entry announces as
          // "Settings 8 tabs" and every test and screen reader has to know about the count.
          aria-hidden
          // A live count churns between a baseline and a run — one more punch at the desk terminal
          // and the number moves — so the visual gate masks it, the way it masks the live chip.
          data-testid="nav-badge"
          className={cn(
            "ml-auto shrink-0 font-mono text-caption",
            badge.tone === "warning"
              ? "rounded-sm bg-warning-soft px-1.5 font-medium text-warning-fg"
              : "text-fg-faint",
          )}
        >
          {badge.tone === "warning" ? `⚠ ${badge.value}` : badge.value}
        </span>
      ) : null}
      {!collapsed && disabledReason ? (
        <span className="ml-auto rounded-sm border border-border px-1 text-caption font-normal text-fg-faint">
          {item.disabledReason ? "Soon" : "Plan"}
        </span>
      ) : null}
    </>
  );
  const node = disabledReason ? (
    <span
      aria-disabled
      title={disabledReason}
      className={cn(
        base,
        drawer ? "h-(--nav-row-h-touch)" : "h-(--nav-row-h-lg)",
        "cursor-not-allowed text-fg-faint",
        collapsed && "justify-center px-0",
      )}
    >
      {content}
    </span>
  ) : (
    // A plain `Link`: every question `NavLink` answers (the class, `aria-current`) is answered above
    // it now, and leaving it in place would mean two disagreeing opinions about which entry is current
    // — its own would still put `aria-current="page"` on the parent of the page being read.
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cn(
        base,
        drawer ? "h-(--nav-row-h-touch)" : "h-(--nav-row-h-lg)",
        collapsed && "justify-center px-0",
        active ? "bg-accent-soft font-medium text-accent-fg" : "text-fg hover:bg-surface",
      )}
    >
      {content}
    </Link>
  );
  /*
    The chevron promises a family, and the flyout is what it promises (pass 07: "Reports becomes a
    flyout listing the six questions"). It replaces the collapsed rail's tooltip for this one entry,
    because a tooltip saying "Reports" beside a list of the six reports would be a label for a label.
  */
  if (item.family && !disabledReason) {
    return (
      <li>
        <NavFlyout items={item.family} label={item.label} activePath={pathname} onNavigate={onNavigate}>
          {node}
        </NavFlyout>
      </li>
    );
  }
  if (!collapsed) return <li>{node}</li>;
  return (
    <li>
      <Tooltip>
        <TooltipTrigger asChild>{node}</TooltipTrigger>
        <TooltipContent side="right">
          {item.label}
          {disabledReason ? ` · ${disabledReason}` : ""}
        </TooltipContent>
      </Tooltip>
    </li>
  );
}

/**
 * **"Who and where", top to bottom** (shell refresh 2026-09-22; ADR-0017 second addendum for what is
 * listed): the brand, the workspace switcher (only when the organisation holds a module), the current
 * workspace's menu, the organisation-level entries that follow the user everywhere, and — new at the
 * foot — the account and organisation control that used to close the top bar. Everything that answers
 * "which organisation, which product, which screen, signed in as whom" is in this one column.
 *
 * It stands on the shell's ground with no border and no surface of its own: the canvas beside it is
 * the edge. `--sidebar-w` 232 px, collapsible to the `--sidebar-w-rail` 52 px icon rail (pass 01
 * boards `1a` and `1c`); the collapse control sits in the brand row, where a reader looks for it.
 *
 * ONE menu at a time, generated from that workspace's entries with permission gates (rules/01 §10).
 * A tenant with no add-ons sees brand, the core nine, the account row.
 */
export function Sidebar({ collapsed, onToggle, drawer = false, onNavigate, className }: SidebarProps) {
  const usage = useEntitlementsQuery();
  const { pathname } = useLocation();
  const { current, available, showSwitcher, addModules, orgLevelItems } = useWorkspaces();
  /**
   * The one count in the nav: terminals that are offline or failing sign-in. It rides on the devices
   * query the shell already makes for the connection chip, so it costs nothing extra.
   */
  const devices = useDevicesQuery();
  const needsAttention = (devices.data ?? []).filter(
    (d) => d.status === "offline" || d.status === "auth_failed",
  ).length;
  const isCollapsed = collapsed && !drawer;
  /**
   * ONE highlighted entry in the whole sidebar, decided once (`activeNavItemId`, longest match wins)
   * rather than by each link about itself — the fix for Visits and Visitors lighting up together.
   *
   * **Both lists are resolved together, deliberately.** They are drawn as two groups because they
   * answer questions about different things (a product, and the organisation), but a reader sees one
   * menu and "where am I" has one answer in it. Resolving them apart would let two entries light up
   * the moment an organisation-level address ever sat under a workspace one — the very shape of this
   * defect, reintroduced in the one place a single component could not catch it. They do not nest
   * today (`/members` and `/settings` against `/gym/*` and `/visitors/*`, and `orgLevelItems` is empty
   * inside Core), and this is how it stays a property of the code rather than of today's route table.
   */
  const activeId = activeNavItemId(pathname, [...current.items, ...orgLevelItems]);
  const toggle = !drawer ? (
    <Button
      variant="ghost"
      size="icon"
      onClick={onToggle}
      aria-expanded={!isCollapsed}
      aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      className="size-7 shrink-0 rounded-md text-fg-faint hover:bg-surface"
    >
      <Icon icon={isCollapsed ? PanelLeftOpen : PanelLeftClose} size={16} />
    </Button>
  ) : null;
  return (
    /*
      **The sidebar is dark in both themes** (overhaul 2026-09-22). Not a second palette: tokens.css
      says a nested `[data-theme]` re-scopes every token for its subtree — the kitchen sink shows
      both themes side by side that way — so the column simply declares the dark theme and every
      class inside it (the accent-soft current row, the muted icons, the warning chip, the focus
      ring, the account row) resolves to its dark value with nothing rewritten. A dark rail beside a
      light sheet is what separates "where I can go" from "what I am looking at" at a glance.
    */
    <nav
      aria-label="Main"
      data-theme="dark"
      className={cn(
        "flex h-full shrink-0 flex-col bg-bg text-fg transition-[width] duration-200 ease-out",
        isCollapsed ? "w-(--sidebar-w-rail)" : "w-(--sidebar-w)",
        className,
      )}
    >
      {/* The brand row is `--bar-h-page` tall — the same as the top bar beside it — so the canvas's
          top edge and the first nav group start on one line across the whole shell. */}
      <div
        className={cn(
          "flex h-(--bar-h-page) shrink-0 items-center",
          isCollapsed ? "flex-col justify-center gap-1 px-1" : "gap-2 pl-4 pr-2",
        )}
      >
        <NavLink
          to={ROUTES.overview}
          className="min-w-0 no-underline"
          aria-label="Go to overview"
          onClick={onNavigate}
        >
          <Brand compact={isCollapsed} size="md" />
        </NavLink>
        {!isCollapsed ? <span className="ml-auto">{toggle}</span> : null}
      </div>
      {isCollapsed ? <div className="flex justify-center pb-1">{toggle}</div> : null}
      {showSwitcher ? (
        <WorkspaceStrip
          current={current}
          available={available}
          collapsed={isCollapsed}
          drawer={drawer}
          addModules={addModules}
          onNavigate={onNavigate}
        />
      ) : null}
      <div className={cn("flex flex-1 flex-col overflow-y-auto", isCollapsed ? "px-2" : "px-3")}>
        {/*
          Group labels (redesign 2026-09, pass 01 board 1a). Eight entries in one undifferentiated
          list is a list you read top to bottom every time; two named groups is a list you aim at.
          Hidden on the rail, where there is no room for a word and the icons are the index.
        */}
        {!isCollapsed ? (
          // "Operate", not the workspace's name: the strip above already says which product this is,
          // and a label repeating it teaches nothing. This one names what the group is FOR.
          <p className="px-2.5 pb-1.5 pt-2 text-label uppercase text-fg-muted">Operate</p>
        ) : null}
        <ul className={cn("flex flex-col gap-px", isCollapsed ? "py-2" : "pb-3")}>
          {current.items.map((item) => (
            <NavEntry
              key={item.id}
              item={item}
              {...(item.id === "devices" && needsAttention > 0
                ? { badge: { value: needsAttention, tone: "warning" as const } }
                : item.id === "settings"
                  ? { badge: { value: `${SETTINGS_TABS.length} tabs`, tone: "quiet" as const } }
                  : {})}
              active={item.id === activeId}
              collapsed={isCollapsed}
              drawer={drawer}
              onNavigate={onNavigate}
              pathname={pathname}
              usage={usage.data}
            />
          ))}
        </ul>
        {orgLevelItems.length > 0 ? (
          // Organisation-level, not product-level: Settings — and inside it billing, members, roles,
          // integrations and the audit log — follows the user into every workspace, because all of
          // those are questions about the organisation rather than about the product in front of them.
          <div className="mt-auto border-t border-border py-2">
            {!isCollapsed ? (
              <p className="px-2.5 pb-1.5 pt-2 text-label uppercase text-fg-muted">Organisation</p>
            ) : null}
            <ul className="flex flex-col gap-px">
              {orgLevelItems.map((item) => (
                <NavEntry
                  key={item.id}
                  item={item}
                  active={item.id === activeId}
                  {...(item.id === "settings"
                    ? { badge: { value: `${SETTINGS_TABS.length} tabs`, tone: "quiet" as const } }
                    : {})}
                  collapsed={isCollapsed}
                  drawer={drawer}
                  pathname={pathname}
                  onNavigate={onNavigate}
                  usage={usage.data}
                />
              ))}
            </ul>
          </div>
        ) : null}
      </div>
      {/*
        The foot: the account and organisation control, moved here from the top bar. It is the last
        thing in the column because it is the one a reader reaches for least and looks for most —
        every product with a sidebar keeps "me" at the bottom-left, and a control in the place people
        expect it is a control nobody has to find.
      */}
      <div className={cn("border-t border-border", isCollapsed ? "px-1 py-2" : "p-2")}>
        <ThemeToggle variant="sidebar" collapsed={isCollapsed} />
        <UserMenu variant="sidebar" collapsed={isCollapsed} />
      </div>
    </nav>
  );
}
